import {
  useRef,
  useState,
  useEffect,
  useCallback,
  MouseEvent,
  TouchEvent
} from 'react'

interface Position {
  x: number
  y: number
}

interface Bounds {
  left: number
  top: number
  right: number
  bottom: number
}

interface UseDraggableOptions {
  defaultPosition?: Position
  bounds?: Bounds
  onDrag?: (e: MouseEvent | TouchEvent, data: { x: number; y: number }) => void
  cancel?: string
}

export const useDraggable = (options: UseDraggableOptions = {}) => {
  const { defaultPosition = { x: 0, y: 0 }, bounds, onDrag, cancel } = options

  const [position, setPosition] = useState<Position>(defaultPosition)
  const [isDragging, setIsDragging] = useState(false)
  const dragRef = useRef<HTMLDivElement>(null)
  const startPosRef = useRef<Position>({ x: 0, y: 0 })
  const offsetRef = useRef<Position>({ x: 0, y: 0 })

  /**
   * Dragging should stay within optional bounds while still letting the
   * overlay move freely when no limits were provided.
   */
  const constrainPosition = useCallback(
    (pos: Position): Position => {
      if (!bounds) return pos

      return {
        x: Math.max(bounds.left, Math.min(pos.x, bounds.right)),
        y: Math.max(bounds.top, Math.min(pos.y, bounds.bottom))
      }
    },
    [bounds]
  )

  const handleMouseDown = (e: MouseEvent<HTMLDivElement>) => {
    if (cancel) {
      const target = e.target as HTMLElement
      if (target.closest(cancel)) {
        return
      }
    }

    e.preventDefault()
    setIsDragging(true)
    startPosRef.current = { x: e.clientX, y: e.clientY }
    offsetRef.current = position
  }

  const handleTouchStart = (e: TouchEvent<HTMLDivElement>) => {
    if (cancel) {
      const target = e.target as HTMLElement
      if (target.closest(cancel)) {
        return
      }
    }

    const touch = e.touches[0]
    setIsDragging(true)
    startPosRef.current = { x: touch.clientX, y: touch.clientY }
    offsetRef.current = position
  }

  const onDragRef = useRef(onDrag)
  onDragRef.current = onDrag

  const constrainPositionRef = useRef(constrainPosition)
  constrainPositionRef.current = constrainPosition

  useEffect(() => {
    const handleMouseMove = (e: globalThis.MouseEvent) => {
      if (!isDragging) return

      const deltaX = e.clientX - startPosRef.current.x
      const deltaY = e.clientY - startPosRef.current.y

      const newPosition = constrainPositionRef.current({
        x: offsetRef.current.x + deltaX,
        y: offsetRef.current.y + deltaY
      })

      setPosition(newPosition)

      if (!onDragRef.current) return
      onDragRef.current(e as any, newPosition)
    }

    const handleTouchMove = (e: globalThis.TouchEvent) => {
      if (!isDragging) return

      const touch = e.touches[0]
      const deltaX = touch.clientX - startPosRef.current.x
      const deltaY = touch.clientY - startPosRef.current.y

      const newPosition = constrainPositionRef.current({
        x: offsetRef.current.x + deltaX,
        y: offsetRef.current.y + deltaY
      })

      setPosition(newPosition)

      if (!onDragRef.current) return
      onDragRef.current(e as any, newPosition)
    }

    const handleEnd = () => {
      setIsDragging(false)
    }

    if (!isDragging) return

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleEnd)
    document.addEventListener('touchmove', handleTouchMove)
    document.addEventListener('touchend', handleEnd)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleEnd)
      document.removeEventListener('touchmove', handleTouchMove)
      document.removeEventListener('touchend', handleEnd)
    }
  }, [isDragging])

  const { x: defaultX, y: defaultY } = defaultPosition

  useEffect(() => {
    setPosition({ x: defaultX, y: defaultY })
  }, [defaultX, defaultY])

  return {
    dragRef,
    position,
    isDragging,
    handlers: {
      onMouseDown: handleMouseDown,
      onTouchStart: handleTouchStart
    }
  }
}
